/**
 * Controles para cambiar el estado del expediente.
 *
 * Render condicional:
 * - Si `identificacionPendiente` (expediente idea-first cuya formulacion
 *   aun no confirma los 6 modulos de identificacion) → mensaje explicando
 *   por que el cambio de estado no esta disponible todavia. No tiene
 *   sentido radicar/avanzar un expediente que ni siquiera tiene su
 *   identificacion MGA lista.
 * - Si el backend no manda transiciones → mensaje "estado final".
 * - Else → botones forward (Avanzar a X) + reverse (Volver a Borrador)
 *   en grupos separados (Sprint 11 follow-up #3).
 * - Que transiciones hay lo decide el backend (`allowed_transitions`, issue
 *   #823); aca solo se ordenan por el ciclo de vida y se agrupan.
 * - draft → submitted bloqueado con tooltip explicativo si hay items
 *   obligatorios pendientes (Sprint 11 follow-up #8).
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { InlineSpinner } from "@/components/InlineSpinner";
import { track } from "@/lib/analytics";
import {
  STATUS_LABELS,
  changeProjectStatus,
  isReverseTransition,
  sortByLifecycle,
  type ProjectDetailDTO,
  type ProjectStatus,
} from "@/lib/api";
import { myPlanQueryKey } from "@/lib/hooks/useActiveProjectsQuota";
import { useActiveMunicipalityDane } from "@/lib/municipality";
import { STATUS_CONTROLS_ANCHOR_ID } from "./focus";

export function StatusControls({
  project,
  onChanged,
  identificacionPendiente = false,
}: {
  project: ProjectDetailDTO;
  onChanged: () => void;
  /**
   * true cuando el expediente nace de una idea (Asistente Formulador) y
   * los 6 modulos de identificacion aun no estan confirmados. Mientras
   * sea true, no ofrecemos cambio de estado — se muestra una explicacion
   * en su lugar (ver `if` abajo). Default false: los expedientes que
   * nacen de una convocatoria (sin formulacion) no se ven afectados y
   * siguen mostrando los controles como siempre.
   */
  identificacionPendiente?: boolean;
}) {
  const activeDane = useActiveMunicipalityDane();
  const queryClient = useQueryClient();
  const allowed = sortByLifecycle(project.allowed_transitions);
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (next: ProjectStatus) =>
      changeProjectStatus(project.id, activeDane, {
        to_status: next,
        notes: notes.trim() || undefined,
      }),
    onSuccess: (_data, next) => {
      track({
        event_name: "project_status_changed",
        properties: {
          project_id: project.id,
          from_status: project.status,
          to_status: next,
        },
      });
      setNotes("");
      setError(null);
      // Cerrar o rechazar libera un cupo del plan, y radicar consume uno
      // (issue #568). Sin esto, el plan cacheado (staleTime 5min) dejaria
      // el boton "Crear expediente" del Radar bloqueado despues de haber
      // liberado el cupo. Va acá y no en `onChanged` del padre porque es
      // consecuencia de esta mutación, no de la vista que la hospeda.
      void queryClient.invalidateQueries({ queryKey: myPlanQueryKey(activeDane) });
      // Rechazar o cerrar libera también la convocatoria, y reabrir la
      // vuelve a ocupar (issue #541): la tarjeta del Radar pasa entre
      // "Ver expediente" y "Crear expediente".
      void queryClient.invalidateQueries({ queryKey: ["opportunities"] });
      onChanged();
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : "Error al cambiar estado");
    },
  });

  if (identificacionPendiente) {
    return (
      <section
        data-testid="status-controls-pending"
        className="rounded-2xl border border-slate-200 bg-slate-50 p-5 text-sm text-slate-600"
      >
        Completa los 6 módulos de identificación para gestionar el estado del expediente.
      </section>
    );
  }

  if (allowed.length === 0) {
    return (
      <section className="rounded-2xl border border-slate-200 bg-slate-50 p-5 text-sm text-slate-600">
        Este expediente está en estado final. No hay más transiciones disponibles.
      </section>
    );
  }

  // Sprint 11 follow-up #8: la transición draft → submitted queda
  // bloqueada cuando hay items obligatorios pendientes. Si el botón
  // se intenta sin completar, el backend responde 422 — pero
  // mejor UX es deshabilitar el botón con tooltip explicativo.
  const pendingRequiredItems = project.checklist
    .filter((item) => item.is_required && !item.is_completed)
    .map((item) => item.title);
  const blockedDraftToSubmit = pendingRequiredItems.length > 0;

  function isTransitionBlocked(next: ProjectStatus): boolean {
    return project.status === "draft" && next === "submitted" && blockedDraftToSubmit;
  }

  function blockedReason(): string {
    const list =
      pendingRequiredItems.length <= 3
        ? pendingRequiredItems.join(", ")
        : `${pendingRequiredItems.slice(0, 3).join(", ")} y ${pendingRequiredItems.length - 3} más`;
    return `Te faltan items obligatorios del checklist: ${list}.`;
  }

  return (
    <section
      id={STATUS_CONTROLS_ANCHOR_ID}
      style={{ scrollMarginTop: "5rem" }}
      aria-label="Cambiar estado"
      data-testid="status-controls"
      data-tour-id="status-controls"
      className="rounded-2xl border border-slate-200 bg-white p-5 shadow-xs"
    >
      <h2 className="mb-2 text-sm font-semibold text-slate-900">Cambiar estado</h2>
      <textarea
        className="mb-3 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500 focus:outline-hidden"
        rows={2}
        placeholder="Notas de la transición (opcional)"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
      />
      {/* Sprint 11 follow-up #3: separamos los botones forward
          (avanzar) de los reverse (volver a Borrador) para que el
          flujo natural del expediente quede prominente y la opción
          de corregir estado se vea como una excepción.

          Sprint 12 polish: cada botón muestra un spinner inline
          mientras `mutation.isPending`. Antes la transición se
          sentía "pegada" 1-2s sin feedback visual.

          Sprint 12 polish: removido el ícono `↶` (no se reconocía)
          y el texto "Útil si marcaste por error." (sobraba). La
          intención queda clara solo con "Volver a Borrador". */}
      <div className="flex flex-wrap gap-2">
        {allowed
          .filter((next) => !isReverseTransition(project.status, next))
          .map((next) => {
            const blocked = isTransitionBlocked(next);
            const tooltip = blocked ? blockedReason() : undefined;
            const isThisPending = mutation.isPending && mutation.variables === next;
            return (
              <button
                key={next}
                type="button"
                disabled={mutation.isPending || blocked}
                title={tooltip}
                aria-disabled={mutation.isPending || blocked}
                data-testid={`status-button-${next}`}
                onClick={() => mutation.mutate(next)}
                className="inline-flex items-center gap-1.5 rounded-md border border-brand-300 bg-brand-50 px-3 py-1.5 text-xs font-semibold text-brand-700 hover:bg-brand-100 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isThisPending && <InlineSpinner tone="brand" />}
                {isThisPending ? "Cambiando…" : `Marcar como ${STATUS_LABELS[next]}`}
              </button>
            );
          })}
      </div>

      {allowed.some((next) => isReverseTransition(project.status, next)) && (
        <div className="mt-4 border-t border-slate-100 pt-3">
          <p className="mb-2 text-[11px] font-semibold tracking-wide text-slate-500 uppercase">
            Corregir estado
          </p>
          <div className="flex flex-wrap items-center gap-2">
            {allowed
              .filter((next) => isReverseTransition(project.status, next))
              .map((next) => {
                const isThisPending = mutation.isPending && mutation.variables === next;
                return (
                  <button
                    key={next}
                    type="button"
                    disabled={mutation.isPending}
                    data-testid={`status-button-${next}-reverse`}
                    onClick={() => mutation.mutate(next)}
                    className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-slate-400 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-50"
                    title="Vuelve a Borrador. La acción queda registrada en el timeline."
                  >
                    {isThisPending && <InlineSpinner tone="slate" />}
                    {isThisPending ? "Volviendo…" : `Volver a ${STATUS_LABELS[next]}`}
                  </button>
                );
              })}
          </div>
        </div>
      )}
      {blockedDraftToSubmit && (
        <p data-testid="status-blocked-hint" className="mt-2 text-xs text-amber-700">
          Para radicar (Borrador → Radicado) primero completa los items obligatorios del checklist:{" "}
          <strong>
            {blockedReason().replace("Te faltan items obligatorios del checklist: ", "")}
          </strong>
        </p>
      )}
      {error && <p className="mt-2 text-xs text-red-700">{error}</p>}
    </section>
  );
}
